/**
 * TesterReportModal - Bug report form for testers
 * 
 * Sends bug reports to Discord via backend webhook.
 * Includes auto-captured device/route context.
 */

import React, { useMemo, useState } from "react";
import { Bug, X, Send, Camera, Copy, Check } from "lucide-react";

function getDeviceLabel() {
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return `${platform} • ${ua}`.slice(0, 350);
}

function getDebugInfo(currentUser) {
  const now = new Date().toISOString();
  return `--- Debug Info ---
Route: ${window.location.pathname}${window.location.search}
Time: ${now}
User: ${currentUser?.email || "not logged in"}
Device: ${getDeviceLabel()}
Build: ${process.env.REACT_APP_BUILD_VERSION || "dev"}
Screen: ${window.innerWidth}x${window.innerHeight}
-------------------`;
}

const SUGGESTED = [
  { label: "Photos not loading", category: "photos", template: "Photos aren't loading on this screen.\n\nSteps:\n1)\n2)\n\nWhat I saw:\n\n" },
  { label: "Video won't play", category: "video", template: "A video won't play.\n\nSteps:\n1)\n2)\n\nWhat I saw:\n\n" },
  { label: "Messages not sending", category: "chat", template: "My message didn't send.\n\nSteps:\n1)\n2)\n\nWhat I saw:\n\n" },
  { label: "Can't login", category: "login", template: "I can't log in.\n\nWhat I tried:\n\nWhat happened:\n\n" },
  { label: "Payment / upgrade issue", category: "payments", template: "Something went wrong with payment or upgrading.\n\nWhat happened:\n\n" },
  { label: "Something looks wrong", category: "ui", template: "Something looks wrong on this screen.\n\nWhat I saw:\n\n" },
];

export default function TesterReportModal({ open, onClose, currentUser }) {
  const [happened, setHappened] = useState("");
  const [expected, setExpected] = useState("");
  const [severity, setSeverity] = useState("bug");
  const [category, setCategory] = useState("other");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const build = useMemo(() => process.env.REACT_APP_BUILD_VERSION || "dev", []);

  if (!open) return null;

  function applySuggestion(s) {
    setCategory(s.category || "other");
    setSeverity("bug");
    setHappened((prev) => (prev?.trim() ? prev : s.template));
    setExpected("");
  }

  async function copyDebugInfo() {
    const info = getDebugInfo(currentUser);
    try {
      await navigator.clipboard.writeText(info);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = info;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function submit() {
    setErr("");
    setDone(false);
    if (!happened.trim()) {
      setErr("Tell us what happened (1 sentence is fine).");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("happened", happened.trim());
      fd.append("expected", expected.trim());
      fd.append("severity", severity);
      fd.append("category", category);
      fd.append("route", window.location.pathname + window.location.search);
      fd.append("device", getDeviceLabel());
      fd.append("build", build);
      fd.append("user_email", currentUser?.email || "");
      fd.append("user_id", currentUser?.id || currentUser?._id || "");
      if (file) fd.append("screenshot", file);

      const token = process.env.REACT_APP_TESTER_REPORT_TOKEN || "";
      const backendUrl = window.location.origin;
      const res = await fetch(`${backendUrl}/api/feedback/tester`, {
        method: "POST",
        headers: { "X-Tester-Token": token },
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || "Couldn't send report.");
      }
      setDone(true);
      setHappened("");
      setExpected("");
      setSeverity("bug");
      setCategory("other");
      setFile(null);
    } catch (e) {
      setErr(e.message || "Couldn't send report.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full sm:max-w-xl bg-slate-900 border border-white/10 rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 m-0 sm:m-4 shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center">
              <Bug className="w-5 h-5 text-rose-400" />
            </div>
            <div>
              <div className="text-white font-semibold text-lg">Report a problem</div>
              <div className="text-white/60 text-sm">
                Your device + screen info is included automatically
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick picks */}
        <div className="mb-4">
          <div className="text-white/70 text-sm mb-2">Quick pick (optional)</div>
          <div className="flex flex-wrap gap-2">
            {SUGGESTED.map((s) => (
              <button
                key={s.label}
                type="button"
                onClick={() => applySuggestion(s)}
                className="px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 text-xs transition-colors"
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="space-y-4">
          {/* Severity & Category */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-white/80 text-sm mb-1">How bad is it?</label>
              <select
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 focus:border-rose-500/50 focus:outline-none"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                <option value="blocking">🚨 Blocking</option>
                <option value="bug">🐞 Bug</option>
                <option value="confusing">❓ Confusing</option>
                <option value="suggestion">💡 Suggestion</option>
              </select>
            </div>
            <div>
              <label className="block text-white/80 text-sm mb-1">Category</label>
              <select
                className="w-full bg-white/5 border border-white/10 text-white rounded-xl p-3 focus:border-rose-500/50 focus:outline-none"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="photos">Photos</option>
                <option value="video">Video</option>
                <option value="chat">Chat</option>
                <option value="login">Login</option>
                <option value="payments">Payments</option>
                <option value="ui">Something looks off</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* What happened */}
          <div>
            <label className="block text-white/80 text-sm mb-1">What happened?</label>
            <textarea
              className="w-full min-h-[120px] bg-white/5 border border-white/10 text-white rounded-xl p-3 placeholder-white/40 focus:border-rose-500/50 focus:outline-none resize-none"
              placeholder="Example: I tapped the second photo and nothing changed."
              value={happened}
              onChange={(e) => setHappened(e.target.value)}
            />
          </div>

          {/* Expected behavior */}
          <div>
            <label className="block text-white/80 text-sm mb-1">What did you expect?</label>
            <textarea
              className="w-full min-h-[80px] bg-white/5 border border-white/10 text-white rounded-xl p-3 placeholder-white/40 focus:border-rose-500/50 focus:outline-none resize-none"
              placeholder="Example: The main photo should switch instantly."
              value={expected}
              onChange={(e) => setExpected(e.target.value)}
            />
          </div>

          {/* Screenshot */}
          <div>
            <label className="block text-white/80 text-sm mb-1">Screenshot (optional)</label>
            <div className="flex items-center gap-3">
              <label className="flex-1 flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
                <Camera className="w-4 h-4 text-white/60" />
                <span className="text-white/70 text-sm">
                  {file ? file.name : "Add screenshot"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </label>
              {file && (
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="p-2 text-white/60 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Error/Success messages */}
          {err && (
            <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          {done && (
            <div className="text-emerald-400 text-sm bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
              ✅ Sent — thank you for helping us improve!
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl py-3 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={submit}
              className="flex-1 bg-rose-500 hover:bg-rose-600 text-white rounded-xl py-3 font-semibold disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                "Sending…"
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send
                </>
              )}
            </button>
          </div>

          {/* Context info + Copy Debug */}
          <div className="flex items-center justify-between pt-1">
            <div className="text-white/40 text-xs">
              Screen: {window.location.pathname}
            </div>
            <button
              type="button"
              onClick={copyDebugInfo}
              className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy debug info</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
